import { useState } from "react";
import type {
  CmmdProjectAgentMember,
  CmmdProjectAgentMemberConsolidationPreview,
  CmmdProjectAgentMemberConsolidationResult,
} from "@cmmd-forge/contracts";

import {
  AlertDialog,
  AlertDialogClose,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";

type ConsolidationState =
  | { readonly kind: "idle" }
  | { readonly kind: "loading" }
  | { readonly kind: "ready"; readonly plan: CmmdProjectAgentMemberConsolidationPreview }
  | {
      readonly kind: "applied";
      readonly plan: CmmdProjectAgentMemberConsolidationPreview;
      readonly result: CmmdProjectAgentMemberConsolidationResult;
    }
  | { readonly kind: "error"; readonly message: string };

export interface AgentMemberConsolidationPanelProps {
  readonly members: readonly CmmdProjectAgentMember[];
  readonly disabled: boolean;
  readonly onPreview: () => Promise<CmmdProjectAgentMemberConsolidationPreview>;
  readonly onApply: (
    expectedPlanHash: string,
  ) => Promise<CmmdProjectAgentMemberConsolidationResult>;
}

export function AgentMemberConsolidationPanel(props: AgentMemberConsolidationPanelProps) {
  const [state, setState] = useState<ConsolidationState>({ kind: "idle" });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const plan = planForState(state);

  const preview = async () => {
    setState({ kind: "loading" });
    try {
      setState({ kind: "ready", plan: await props.onPreview() });
    } catch (cause) {
      setState({ kind: "error", message: formatError(cause, "Team-seat preview is unavailable.") });
    }
  };

  const apply = async () => {
    if (!plan) return;
    setConfirmOpen(false);
    setState({ kind: "loading" });
    try {
      setState({ kind: "applied", plan, result: await props.onApply(plan.planHash) });
    } catch (cause) {
      setState({
        kind: "error",
        message: formatError(cause, "Team-seat cleanup was not applied."),
      });
    }
  };

  return (
    <section
      className="rounded-lg border border-border bg-muted/30 p-3"
      aria-labelledby="team-seat-cleanup-heading"
    >
      <ConsolidationHeader
        disabled={props.disabled || state.kind === "loading"}
        loading={state.kind === "loading"}
        onReviewPlan={preview}
      />
      <ConsolidationFeedback members={props.members} state={state} plan={plan} />
      <ConsolidationApplyAction
        state={state}
        plan={plan}
        onOpenConfirmation={() => setConfirmOpen(true)}
      />
      <ApplyConsolidationDialog
        open={confirmOpen}
        pending={state.kind === "loading"}
        retirementCount={plan ? retirementCount(plan) : 0}
        onOpenChange={setConfirmOpen}
        onConfirm={apply}
      />
    </section>
  );
}

function ConsolidationHeader(props: {
  readonly disabled: boolean;
  readonly loading: boolean;
  readonly onReviewPlan: () => Promise<void>;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div>
        <h4 id="team-seat-cleanup-heading" className="text-sm font-semibold">
          Team seat cleanup
        </h4>
        <p className="mt-1 text-xs leading-5 text-muted-foreground">
          Review CMMD&apos;s locked plan before it retires legacy seats. Project Lead and history
          remain intact.
        </p>
      </div>
      <Button
        size="sm"
        variant="outline"
        disabled={props.disabled}
        onClick={() => void props.onReviewPlan()}
      >
        {props.loading ? "Reviewing…" : "Review plan"}
      </Button>
    </div>
  );
}

function ConsolidationFeedback(props: {
  readonly members: readonly CmmdProjectAgentMember[];
  readonly state: ConsolidationState;
  readonly plan: CmmdProjectAgentMemberConsolidationPreview | null;
}) {
  return (
    <>
      {props.state.kind === "error" ? <ConsolidationError message={props.state.message} /> : null}
      {props.plan ? <ConsolidationPlan members={props.members} plan={props.plan} /> : null}
      {props.state.kind === "applied" ? <ConsolidationReceipt result={props.state.result} /> : null}
    </>
  );
}

function ConsolidationError(props: { readonly message: string }) {
  return (
    <p role="alert" className="mt-3 text-sm text-destructive">
      {props.message}
    </p>
  );
}

function ConsolidationReceipt(props: {
  readonly result: CmmdProjectAgentMemberConsolidationResult;
}) {
  const retiredCount = props.result.retiredSeatIds.length;
  const assignmentCount = props.result.transferredAssignmentCount;
  return (
    <p role="status" className="mt-3 text-sm text-muted-foreground">
      Applied receipt: retired {retiredCount} seat{retiredCount === 1 ? "" : "s"} and transferred{" "}
      {assignmentCount} provider assignment{assignmentCount === 1 ? "" : "s"}.
    </p>
  );
}

function ConsolidationApplyAction(props: {
  readonly state: ConsolidationState;
  readonly plan: CmmdProjectAgentMemberConsolidationPreview | null;
  readonly onOpenConfirmation: () => void;
}) {
  const canApply = props.state.kind === "ready" && props.plan !== null && canApplyPlan(props.plan);
  return canApply ? (
    <div className="mt-3 flex justify-end">
      <Button variant="destructive" size="sm" onClick={props.onOpenConfirmation}>
        Retire reviewed seats
      </Button>
    </div>
  ) : null;
}

function ConsolidationPlan(props: {
  readonly members: readonly CmmdProjectAgentMember[];
  readonly plan: CmmdProjectAgentMemberConsolidationPreview;
}) {
  return (
    <ul className="mt-3 divide-y divide-border rounded-md border border-border bg-card text-sm">
      {props.plan.groups.map((group) => (
        <li key={group.agentKind} className="p-3">
          <p className="font-medium capitalize">{group.agentKind}</p>
          {group.blocker ? (
            <p className="mt-1 text-xs text-destructive">
              Cannot apply: {formatBlocker(group.blocker)}.
            </p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">
              {group.assignmentHandling === "require_unassigned"
                ? `Retire legacy Forge test seats: ${seatLabels(props.members, group.duplicateSeatIds)}. They must have no active task assignment.`
                : `Keep ${seatLabel(props.members, group.canonicalSeatId)}; retire ${seatLabels(props.members, group.duplicateSeatIds)}.`}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
}

function ApplyConsolidationDialog(props: {
  readonly open: boolean;
  readonly pending: boolean;
  readonly retirementCount: number;
  readonly onOpenChange: (open: boolean) => void;
  readonly onConfirm: () => Promise<void>;
}) {
  return (
    <AlertDialog open={props.open} onOpenChange={props.onOpenChange}>
      <AlertDialogPopup className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Retire {props.retirementCount} reviewed seats?</AlertDialogTitle>
          <AlertDialogDescription>
            CMMD will re-read this plan under its lifecycle lock. It refuses stale plans, live
            controller/reservation/authority claims, and active assignments for legacy Forge test
            seats. Historical identities remain resolvable.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogClose render={<Button variant="outline" />} disabled={props.pending}>
            Cancel
          </AlertDialogClose>
          <Button
            variant="destructive"
            disabled={props.pending}
            onClick={() => void props.onConfirm()}
          >
            {props.pending ? "Applying…" : "Apply reviewed plan"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogPopup>
    </AlertDialog>
  );
}

function planForState(
  state: ConsolidationState,
): CmmdProjectAgentMemberConsolidationPreview | null {
  return state.kind === "ready" || state.kind === "applied" ? state.plan : null;
}

function canApplyPlan(plan: CmmdProjectAgentMemberConsolidationPreview): boolean {
  return !hasBlocker(plan) && retirementCount(plan) > 0;
}

function retirementCount(plan: CmmdProjectAgentMemberConsolidationPreview): number {
  return plan.groups.reduce((total, group) => total + group.duplicateSeatIds.length, 0);
}
function hasBlocker(plan: CmmdProjectAgentMemberConsolidationPreview): boolean {
  return plan.groups.some((group) => group.blocker !== null);
}
function seatLabel(members: readonly CmmdProjectAgentMember[], seatId: number | null): string {
  if (seatId === null) return "no canonical seat";
  const member = members.find((candidate) => candidate.id === seatId);
  return member ? `${member.displayName} (${member.agentId})` : `seat #${seatId}`;
}
function seatLabels(
  members: readonly CmmdProjectAgentMember[],
  seatIds: readonly number[],
): string {
  return seatIds.length === 0
    ? "none"
    : seatIds.map((seatId) => seatLabel(members, seatId)).join(", ");
}
function formatBlocker(blocker: "canonical_seat_missing"): string {
  return blocker.replaceAll("_", " ");
}
function formatError(cause: unknown, fallback: string): string {
  return cause instanceof Error && cause.message.trim() ? cause.message : fallback;
}
